import gql from "graphql-tag";
import { Company, Fragment, Campaign, Mad, Viewer } from "../types";
import {
  ContractTemplateSummaryFragment,
  CONTRACT_TEMPLATE_SUMMARY_FRAGMENT,
} from "./ContractTemplate";
import { GROUP_FRAGMENT, GroupFragment } from "./Group";
import {
  QuestionOptionsConfigurationFragment,
  QUESTION_OPTIONS_CONFIGURATION_FRAGMENT,
} from "./QuestionOptionsConfiguration";
import { ThemeFragment, THEME_FRAGMENT } from "./Theme";
import { UserSummaryFragment, USER_SUMMARY_FRAGMENT } from "./User";

export const VIEWER_FRAGMENT = gql`
  fragment ViewerFragment on Viewer {
    id
    slug
    name
    locale
    companyId
    function
    email
    avatar
    role
    isCapableOfInsertingGroups
    isCapableOfInsertingMads
    isCapableOfInsertingCampaigns
    isCapableOfInsertingContractTemplates
    isCapableOfInsertingTemplates
    isCapableOfInsertingUsers
    isCapableOfInsertingManagers
    isCapableOfInsertingAdmins
    isCapableOfReadingGroups
    isCapableOfReadingMads
    isCapableOfReadingCampaigns
    isCapableOfReadingContractTemplates
    isCapableOfReadingTemplates
    isCapableOfReadingUsers
    isCapableOfReadingActivities
    isCapableOfUpdatingCompanies
    isCapableOfReadingUserNotes
    isCapableOfUpdatingUserNotes
    isCapableOfSigningReports
    company {
      id
      name
      locale
      contractsEnabled
      reminderEnabled
      reminderGap
      localeNamespace
      questionOptionsConfigurations {
        ...QuestionOptionsConfigurationFragment
      }
      users {
        ...UserSummaryFragment
      }
      contractTemplates {
        ...ContractTemplateSummaryFragment
      }
      campaigns {
        id
        slug
        name
        description
        companyId
        canInsert
        canShow
        reminderGap
        reminderEnabled
        # NOTE: Should be removed in favor of companyId
        company {
          id
        }
        madsCount
        mads {
          id
          talent {
            ...UserSummaryFragment
          }
        }
        unfilledReportsCount
        uncheckedReportsCount
        reportsCount
      }
      groups {
        ...GroupFragment
      }
      visualIdentity
      theme {
        ...ThemeFragment
      }
    }
  }

  ${GROUP_FRAGMENT}
  ${QUESTION_OPTIONS_CONFIGURATION_FRAGMENT}
  ${USER_SUMMARY_FRAGMENT}
  ${CONTRACT_TEMPLATE_SUMMARY_FRAGMENT}
  ${THEME_FRAGMENT}
`;

// prettier-ignore
export type ViewerFragment = Fragment<
  Viewer,
  Pick<Viewer,
      "id" |
      "slug" |
      "email" |
      "name" |
      "function" |
      "avatar" |
      "locale" |
      "companyId" |
      "role" |
      "isCapableOfInsertingGroups" |
      "isCapableOfInsertingMads" |
      "isCapableOfInsertingCampaigns" |
      "isCapableOfInsertingContractTemplates" |
      "isCapableOfInsertingTemplates" |
      "isCapableOfInsertingUsers" |
      "isCapableOfInsertingManagers" |
      "isCapableOfInsertingAdmins" |
      "isCapableOfReadingGroups" |
      "isCapableOfReadingMads" |
      "isCapableOfReadingCampaigns" |
      "isCapableOfReadingContractTemplates" |
      "isCapableOfReadingTemplates" |
      "isCapableOfReadingUsers" |
      "isCapableOfReadingActivities" | 
      "isCapableOfUpdatingCompanies" |
      "isCapableOfReadingUserNotes" |
      "isCapableOfUpdatingUserNotes" |
      "isCapableOfSigningReports"
    >
    & {
      company: Pick<Company, 'id' | 'name' | 'visualIdentity' | 'logo' | 'locale' | 'contractsEnabled' | 'reminderEnabled' | 'reminderGap' | 'localeNamespace'>
        & {
          users: UserSummaryFragment[];
          groups: GroupFragment[];
          contractTemplates: ContractTemplateSummaryFragment[];
          questionOptionsConfigurations: QuestionOptionsConfigurationFragment[];
          campaigns: (Pick<Campaign, 'id' | 'slug' | 'name' | 'description' | 'madsCount' | 'reminderEnabled' | 'reminderGap' | 'companyId' | 'canInsert' | 'canShow' | 'unfilledReportsCount' | 'uncheckedReportsCount' | 'reportsCount'>
            & {
              company: Pick<Company, 'id'>;
              mads: (Pick<Mad, 'id'>
                & {
                  talent: UserSummaryFragment;
                }) []
            })[];
          theme: ThemeFragment;
        }
    }
>;
